// EduBridge Network — Purpose-built dashboard cards per role.
// Backed by /api/dashboard/role-view which adapts its payload to the caller.
// Renders different card layouts + tables for local_admin / team_admin /
// counselor / visa_officer / staff.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Users, UserPlus, GraduationCap, Building2, ShieldCheck, Briefcase, LayoutDashboard, TrendingUp, ClipboardList, CheckCircle2, Clock } from 'lucide-react';

const CARD_ICON = {
  teams: Building2,
  team_members: Users,
  team_admins: ShieldCheck,
  counselors: Users,
  visa_officers: Briefcase,
  agents: UserPlus,
  students: GraduationCap,
  users: Users,
  applications: ClipboardList,
  my_applications: LayoutDashboard,
  my_cases: Briefcase,
  pending: Clock,
  pending_review: Clock,
  completed: CheckCircle2,
  in_progress: TrendingUp,
  on_visa_desk: Briefcase,
};

const StatCard = ({ card }) => {
  const Icon = CARD_ICON[card.key] || LayoutDashboard;
  return (
    <Card data-testid={`role-dash-card-${card.key}`}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{card.label}</div>
          <div className="text-2xl font-bold text-[#1A2A4F] dark:text-slate-100">{card.value ?? 0}</div>
          {card.sublabel && <div className="text-[11px] text-muted-foreground mt-0.5">{card.sublabel}</div>}
        </div>
      </CardContent>
    </Card>
  );
};

const StatusPills = ({ by_status, title = 'Applications by Status' }) => {
  const entries = Object.entries(by_status || {}).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <Badge key={k} variant="secondary" className="capitalize" data-testid={`role-dash-status-${k}`}>
            {(k || 'unset').replace(/_/g, ' ')}: <span className="ml-1 font-bold">{v}</span>
          </Badge>
        ))}
      </CardContent>
    </Card>
  );
};

const DeskPills = ({ by_desk }) => {
  const entries = Object.entries(by_desk || {}).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-sm">Applications by Desk</CardTitle></CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <Badge key={k} variant="outline" className="capitalize" data-testid={`role-dash-desk-${k}`}>
            {(k || 'unset').replace(/_/g, ' ')}: <span className="ml-1 font-bold">{v}</span>
          </Badge>
        ))}
      </CardContent>
    </Card>
  );
};

const PerTeamList = ({ per_team }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-sm">Applications per Team</CardTitle>
      <CardDescription className="text-xs">Where the workspace&apos;s applications live right now.</CardDescription>
    </CardHeader>
    <CardContent>
      {per_team.length === 0 ? (
        <p className="text-sm text-muted-foreground">No team data yet.</p>
      ) : (
        <div className="space-y-2">
          {per_team.map((t) => (
            <div key={t.team_id} className="flex items-center justify-between border-b last:border-b-0 pb-2 last:pb-0" data-testid={`role-dash-team-row-${t.team_id}`}>
              <span className="text-sm font-medium">{t.team_name}</span>
              <Badge variant="secondary">{t.count}</Badge>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const BreakdownList = ({ title, items, idKey, nameKey, countKey, emptyText, testPrefix }) => (
  <Card>
    <CardHeader className="pb-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent>
      {(!items || items.length === 0) ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it[idKey]} className="flex items-center justify-between border-b last:border-b-0 pb-2 last:pb-0" data-testid={`${testPrefix}-${it[idKey]}`}>
              <span className="text-sm font-medium">{it[nameKey]}</span>
              <Badge variant="secondary">{it[countKey]}</Badge>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const RoleDashboard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api().get('/dashboard/role-view')
      .then((res) => {
        if (!mounted) return;
        if (res.data?.redirect) { navigate(res.data.redirect); return; }
        setData(res.data);
      })
      .catch(() => setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground gap-2" data-testid="role-dash-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Building your dashboard…
      </div>
    );
  }
  if (!data) return null;

  const titleByView = {
    local_admin: 'Workspace Analytics',
    team_admin: `${data.team?.name || 'Your Team'} Analytics`,
    counselor: 'My Analytics',
    visa_officer: 'My Visa Cases',
    staff: 'My Analytics',
  };
  const subtitleByView = {
    local_admin: 'Bird\u2019s-eye view of every team, user and application in this workspace.',
    team_admin: 'Everything happening inside your team — counselors, agents, applications.',
    counselor: 'Only applications assigned to you.',
    visa_officer: 'Only visa cases assigned to you.',
    staff: 'Applications you own or that are assigned to you.',
  };

  return (
    <div className="space-y-6" data-testid={`role-dashboard-${data.view}`}>
      <div>
        <h2 className="text-xl font-bold text-[#1A2A4F] dark:text-slate-100">{titleByView[data.view] || 'My Analytics'}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{subtitleByView[data.view]}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {(data.cards || []).map((c) => <StatCard key={c.key} card={c} />)}
      </div>

      {(data.by_status || data.by_desk) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <StatusPills by_status={data.by_status} />
          <DeskPills by_desk={data.by_desk} />
        </div>
      )}

      {data.view === 'local_admin' && data.per_team && (
        <PerTeamList per_team={data.per_team} />
      )}

      {data.view === 'team_admin' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BreakdownList
            title="Applications per Counselor"
            items={data.per_counselor}
            idKey="counselor_id"
            nameKey="counselor_name"
            countKey="count"
            emptyText="No counselors in this team yet."
            testPrefix="role-dash-counselor-row"
          />
          <BreakdownList
            title="Top Agents (this team)"
            items={data.per_agent}
            idKey="agent_id"
            nameKey="agent_name"
            countKey="count"
            emptyText="No agent submissions yet."
            testPrefix="role-dash-agent-row"
          />
        </div>
      )}
    </div>
  );
};

export default RoleDashboard;
